<template>
  <div
    id="app"
    class="google-hope-app"
    v-show="!platform.prerenderer && isReady"
    :version="version"
    :commit-hash="commitHash"
    :deploy-flag="deployFlag"
    :target-domain="targetDomain"
    >

    <transition>
      <div class="header" v-if="currentMode !== 'intro'" :class="{'in-totem': currentMode === 'totem' && !openAbout || currentMode === 'ar', 'page-about': openAbout}">
        <span class="logo" data-block-interaction>BUILDING HOPES</span>

        <transition mode="out-in">
          <span class="share-desktop hover-link" data-block-interaction v-if="backend.shareAvailable && shareOpenAlpha === 0 || (finalModelShow)" @click="onShareClick">SHARE</span>
          <span class="share-desktop hover-link" data-block-interaction v-if="backend.shareAvailable && shareOpenAlpha > 0 && !finalModelShow" @click="onShareClose">BACK</span>
        </transition>

        <transition mode="out-in">
          <span v-if="!openAbout" class="about hover-link" @click="onAboutClick" data-block-interaction>ABOUT</span>
          <span v-else class="about hover-link" @click="onAboutClose" data-block-interaction>BACK</span>
        </transition>

        <transition mode="out-in">
          <span v-if="!openAbout" @click="onAboutClick" class="about-mobile" data-block-interaction>i</span>
          <span v-else @click="onAboutClose" class="about-mobile" data-block-interaction>✕</span>
        </transition>

        <transition>
          <span v-if="openAbout" class="about-label">ABOUT</span>
        </transition>
      </div>
    </transition>

    <transition>
      <HeaderStonesContainer class="stones-container" :concepts="selectedConcepts" v-if="(currentMode !== 'intro' && currentMode !== 'ar') && !totemReady" />
    </transition>
    <!-- 3d canvas draw -->
    <transition>
        <!-- :dragHintVisible.sync="totemDragHintVisible" -->
      <TotemCanvas
        class="totem-canvas"
        :class="{'totem-visualization': currentMode === 'totem'}"
        v-if="currentMode === 'totem' || currentMode === 'wheel' || currentMode === 'save'"
        :style="canvasStyle"
        :mode="currentMode"
        :totems="totems"
        :blockedResize="blockedResize"
        :wheelConcepts="confirmedConcepts"
        :selectedConcepts="selectedConcepts"
        :currentTotem.sync="currentlySelectedTotem"
        :multiTotem.sync="canvasMultiTotem"
        :explodeTotem.sync="canvasExplodeTotem"
        :tooltips.sync="tooltips"
        :detailsPanelConcept.sync="clickedConcept"
        :inactivityTime.sync="inactivityTime"
        :shareOpenAlpha.sync="shareOpenAlpha"
        :wheelBlock="elasticShareOpenAlpha.value > 0.5"
        @concept:click="onConceptClick"
        @finalmodal:show="onFinalModelShow"
        @totemReady="totemReady = true"
        />
    </transition>

    <transition>
      <TotemDotsIndicator
        class="totem-dots"
        :multiTotem.sync="canvasMultiTotem"
        :explodeTotem.sync="canvasExplodeTotem"
        v-if="currentMode === 'totem' && totemReady && shareOpenAlpha < 0.5"
        />
    </transition>

    <!-- concept selection wheel -->
    <transition name="wheel">
      <CanvasConceptsSelectionWheel
        class="selection-wheel"
        v-if="currentMode === 'wheel'"
        :concepts="concepts"
        :forMore="!confirmingConcept && currentMode === 'wheel' && selectedConcepts.length >= 4 && selectedConcepts.length < 10"
        :selected.sync="selectedConcepts"
        :positions.sync="wheelPositions"
        :inactivityTime.sync="inactivityTime"
        :preventBlurOnDesktop="true"
        />
    </transition>

    <!-- AR totem page -->
    <transition>
      <TotemCanvasAR :totemData="currentlySelectedTotem" v-if="currentMode === 'ar' && !markerInstructionsShown" />
    </transition>

    <transition>
      <IntroPageAR v-if="currentMode === 'ar' && !arIntro" @download:click="onDownloadClick" />
    </transition>

    <transition :name="(!confirmingConcept && currentMode === 'wheel' && selectedConcepts.length >= 4 && selectedConcepts.length < 10) ? 'concept-complete' : ''">
      <ConceptImportanceUI
        v-if="confirmingConcept"
        :confirmingConcept="confirmingConcept"
        @confirm="confirmConcept(confirmingConcept)"
        @cancel="removeConcept(confirmingConcept)"
        />
      </transition>

    <!-- selection wheel done button -->
    <div class="wheel-done-bg" v-if="currentMode === 'wheel' && confirmedConcepts.length > 3 && !confirmingConcept"></div>

    <transition name="wheel-done">
      <el-button  
        v-if="currentMode === 'wheel' && confirmedConcepts.length > 3 && !confirmingConcept"
        class="wheel-done-btn"
        @click="gotoSaveTotemForm"
        >
        DONE
      </el-button>
    </transition>

    <!-- switch to AR view -->
    <transition>
      <el-button v-if="currentMode === 'totem' && $root.platform.mobile && webRTCSupported && shareOpenAlpha < 0.5" class="ar-view-btn" @click="switchAR">AR VIEW</el-button>
    </transition>

    <transition>
      <el-button v-if="currentMode === 'totem' && $root.platform.mobile && shareOpenAlpha < 0.5" class="info-btn" @click="onAboutClick">i</el-button>
    </transition>

    <!-- close AR view -->
    <transition>
      <div v-if="currentMode === 'ar' && !markerInstructionsShown" class="ar-close-btn" @click="switchAR"><i class="el-icon-close" /></div>
    </transition>

    <!-- donwload marker in AR view -->
    <transition>
      <a v-if="currentMode === 'ar' && !markerInstructionsShown" class="ar-download-marker-btn" href="/static/img/marker.png" target="_blank">Download marker</a>
    </transition>

    <transition>
      <HintsContainer
        v-show="shareOpenAlpha < 0.5"
        :mode="currentMode"
        :inactivityTime="inactivityTime"
        :selectedConcepts="selectedConcepts"
        :selectedTotem="currentlySelectedTotem"
        :confirmingConcept="confirmingConcept"
        :totems="totems"
        :conceptDetailsPanel="clickedConcept"
        :multiTotem="canvasMultiTotem"
        :explodeTotem="canvasExplodeTotem"
        :clickTarget="null"
        :totemReady="totemReady"
        @scroll:up="onScrollUp"
        @scroll:down="onScrollDown"
        />
    </transition>


    <!-- stone tooltips -->
    <transition-group>
      <div v-if="currentMode === 'totem' && shareOpenAlpha < 0.5" v-for="(tooltip, index) in tooltips":key="tooltip.label" class="tooltip" :style="tooltipStyle(tooltip)">
        <div class="tooltip-internal">{{ getTootipLabel(tooltip.label) }}</div>
      </div>
    </transition-group>

    <!-- AR marker instructions -->
    <transition>
      <ARMarkerInstructions v-if="currentMode === 'ar' && markerInstructionsShown" :shown.sync="markerInstructionsShown" @cancel="currentMode = 'totem'" />
    </transition>

    <!-- concept details panel on totem view -->
    <transition name="details-panel" mode="out-in">
      <keep-alive>
        <ConceptDetailsPanel
          @about:click="onAboutClick"
          @concept:next="onConceptNext"
          @concept:prev="onConceptPrev"
          v-if="clickedConcept"
          :concept.sync="clickedConcept" />
      </keep-alive>
    </transition>

    <!-- intro  -->
    <transition name="intro">
      <IntroScreen class="intro-screen" v-if="currentMode === 'intro' && isReady" :mode.sync="currentMode" />
    </transition>

    <transition name="intro-text">
      <div class="intro-text" v-if="introTextVisible">
        <span>WHAT ARE YOU HOPEFUL FOR?</span>
      </div>
    </transition>

    <!-- totem name save form -->
    <transition>
      <TotemSaveForm
        @input:focus="onFocus"
        @input:blur="onBlur"
        @totem:save="onTotemSave"
        :mode.sync="currentMode"
        v-if="currentMode === 'save'"
        :concepts="selectedConcepts"
      />
    </transition>

    <transition>
      <AboutPage v-if="openAbout" />
    </transition>

    <SharePanel v-if="elasticShareOpenAlpha.value > 0 && !finalModelShow" :totemData="totemData" :openAlpha.sync="shareOpenAlpha" :style="sharePanelStyle" />
    
    <FinalSharePanel
      v-if="elasticShareOpenAlpha.value > 0 && finalModelShow"
      :totemData="totemData"
      :openAlpha.sync="shareOpenAlpha"
      :style="sharePanelStyle"
      @share:open="onShareClick"
      @panel:close="onFinalModelClose"
    />

    <VersionInfo :version="version" v-if="!deployFlag" :commit-hash="commitHash" />
    <!-- <Stats /> -->
  </div>
</template>

<script>
import VersionInfo from '@/atoms/VersionInfo.vue'
import Platform from '@/platform'
import Viewport from '@/mixins/Viewport'
import ARMarkerInstructions from '@/organisms/ARMarkerInstructions'
import TotemCanvasAR from '@/molecules/TotemCanvasAR'
import FirePrerenderEvent from '@/mixins/FirePrerenderEvent'
import TotemCanvas from '@/molecules/TotemCanvas'
import ConceptsSelectionWheel from '@/molecules/ConceptsSelectionWheel'
import CanvasConceptsSelectionWheel from '@/molecules/CanvasConceptsSelectionWheel'
import ConceptDetailsPanel from '@/organisms/ConceptDetailsPanel'
import TotemSaveForm from '@/organisms/TotemSaveForm'
import GoogleHopeService from '@/services/GoogleHopeService'
import IntroScreen from '@/organisms/IntroScreen'
import filter from 'lodash/filter'
import Stats from '@/atoms/Stats'
import ConceptImportanceUI from '@/organisms/ConceptImportanceUI'
import AboutPage from '@/organisms/AboutPage'
import SharePanel from '@/organisms/SharePanel'
import FinalSharePanel from '@/organisms/FinalSharePanel'
import HeaderStonesContainer from '@/molecules/HeaderStonesContainer'
import TotemDotsIndicator from '@/atoms/TotemDotsIndicator'
import HintsContainer from '@/atoms/HintsContainer'
import IntroPageAR from '@/atoms/IntroPageAR'
import ElButton from 'element-ui/packages/button'
import delay from 'lodash/delay'
import RAF from '@/utils/RAF'
import ElasticNumber from '@/utils/ElasticNumber'
// import DetectRTC from 'detectrtc'

export default {
  name: 'App',

  components: {
    VersionInfo,
    TotemCanvas,
    ConceptsSelectionWheel,
    CanvasConceptsSelectionWheel,
    TotemCanvasAR,
    ARMarkerInstructions,
    ConceptDetailsPanel,
    IntroScreen,
    AboutPage,
    IntroPageAR,
    TotemSaveForm,
    ConceptImportanceUI,
    HeaderStonesContainer,
    SharePanel,
    FinalSharePanel,
    TotemDotsIndicator,
    Stats,
    HintsContainer,
    ElButton
  },

  mixins: [FirePrerenderEvent, Viewport],

  props: {
    // version string of this specific build
    version: {
      type: String,
      required: true
    },
    // commit hash of this specific build
    commitHash: {
      type: String,
      required: true
    },
    // this is the domain where the site will stay
    // it varies from build to build so the site can be built to be hosted
    // on various domains
    // it can be set by using
    // npm run build --domain="http://www.example.com"
    targetDomain: {
      type: String,
      required: true
    },
    // this is a flag set to true
    // by the build system (or if you build the site with --deploy)
    // use this to enable debug stuff that needs to be hidden in the deploy version
    deployFlag: {
      type: Boolean,
      required: true
    },
    // the preloader instance used by the root app
    // set preloader.visible to show it / hide it
    preloader: {
      type: Object,
      required: true
    }
  },

  data: function () {
    return {
      // this flag is used to show the app once the preloading
      // (if present) is finished
      isReady: false,
      // platform, accessible with an import statement
      // or by using this.$root.platform
      // NOTE: import statement is preferred
      platform: Platform,
      concepts: [],
      selectedConcepts: [],
      wheelPositions: [],
      currentMode: 'intro',
      markerInstructionsShown: true,
      clickedConcept: null,
      openAbout: false,
      relatedTotems: [],
      backend: GoogleHopeService,
      currentlySelectedTotem: null,
      totemReady: false,
      canvasMultiTotem: 0,
      canvasExplodeTotem: 0,
      blockedResize: false,
      finalModelShow: false,
      arIntro: false,
      tooltips: [],
      preventTotemScrollHint: false,
      introTextVisible: false,
      totemData: {},
      // totemDragHintVisible: false,
      inactivityTime: 0,
      shareOpenAlpha: 0,
      elasticShareOpenAlpha: new ElasticNumber(0),
      webRTCSupported: (!!window.RTCPeerConnection || !!window.mozRTCPeerConnection || !!window.webkitRTCPeerConnection) && !!navigator.mediaDevices
    }
  },

  mounted: async function () {
    await this.backend.getCategories()
    this.concepts = await this.backend.getConcepts()

    this.elasticShareOpenAlpha.speed = 10
    RAF.add(() => {
      this.elasticShareOpenAlpha.target = this.shareOpenAlpha
      this.elasticShareOpenAlpha.update(1 / 60)
    })

    window.onbeforeunload = () => {
      return 'Are you sure you want to leave?'
    }

    // set this.isReady = true
    // when all stuff that needs to be loaded for the app is loaded
    // if you need to preload stuff, delete this line and set to true later
    this.isReady = true
  },

  computed: {
    sharePanelStyle: function () {
      if (this.elasticShareOpenAlpha.value === 0) return {}
      return {
        transform: 'translate(' + ((1 - this.elasticShareOpenAlpha.value) * 100) + '%, 0)'
      }
    },
    canvasStyle: function () {
      if (this.elasticShareOpenAlpha.value === 0) return {}
      return {
        filter: 'blur(' + Math.round(this.elasticShareOpenAlpha.value * 20) + 'px)'
      }
    },
    userTotem: function () {
      // generate totem data here
      let totem = this.backend.userTotem || this.backend.getTotemData(this.selectedConcepts)
      if (totem && totem.stones.length > 0) {
        // totem.id = 'user-totem'
        totem.position = {x: 0, z: 0}
        return totem
      }
      return null
    },
    totems: function () {
      return this.userTotem ? [this.userTotem].concat(this.relatedTotems) : []
    },
    confirmingConcept: function () {
      let ret = null
      this.selectedConcepts.some((concept) => {
        // check if concept is not confirmed
        if (!concept.confirmed) {
          ret = concept
          return true
        }
      })
      return ret
    },
    // check if the concept is confirmed
    confirmedConcepts: function () {
      return filter(this.wheelPositions, {concept: {confirmed: true}})
    }
  },

  methods: {
    onScrollUp () {
      this.canvasMultiTotem = 1
      this.canvasExplodeTotem = 0
    },

    onScrollDown () {
      this.canvasMultiTotem = 0
      this.canvasExplodeTotem = 1
    },

    tooltipStyle: function (tootip) {
      let color = tootip.categoryObject ? tootip.categoryObject.color : tootip.color
      return {
        transform: 'translate(' + tootip.x + 'px, ' + tootip.y + 'px)',
        color: tootip ? '#' + color.toString(16) : null
      }
    },
    getTootipLabel (label) {
      if (Platform.mobile && label.length > 20) {
        return label.slice(0, 18) + '...'
      }

      return label
    },
    onDownloadClick () {
      this.arIntro = true
    },
    onFocus () {
      this.blockedResize = true
    },
    onBlur () {
      this.blockedResize = false
    },
    onTotemSave (data) {
      this.totemData = data
    },
    onConceptClick: function (concept) {
      // console.log('concept click', concept)
      this.clickedConcept = concept
    },
    onFinalModelShow () {
      this.shareOpenAlpha = 1
      this.openAbout = false
      this.finalModelShow = true
    },
    onFinalModelClose () {
      this.shareOpenAlpha = 0
      this.openAbout = false
      this.finalModelShow = false
    },
    confirmConcept: function (concept) {
      concept.confirmed = true
    },
    removeConcept: function (concept) {
      concept.importance = 0.5
      concept.confirmed = false
      let index = this.selectedConcepts.indexOf(concept)
      if (index !== -1) {
        this.selectedConcepts.splice(index, 1)
      }
    },

    onAboutClick () {
      this.openAbout = true
      this.shareOpenAlpha = 0
    },

    onShareClick () {
      this.shareOpenAlpha = 0
      this.openAbout = false
      this.finalModelShow = false

      setTimeout(() => {
        this.shareOpenAlpha = 1
      }, 500)
    },

    onShareClose () {
      this.shareOpenAlpha = 0
    },

    onConceptNext () {
      let allConcepts = []
      this.currentlySelectedTotem.stones.forEach((stone) => {
        stone.concepts.forEach((concept) => {
          allConcepts.push(concept)
        })
      })

      let index = allConcepts.indexOf(this.clickedConcept)

      let nextConcept = allConcepts[(index + 1) % allConcepts.length]
      this.clickedConcept = nextConcept
    },

    onConceptPrev () {
      let allConcepts = []
      this.currentlySelectedTotem.stones.forEach((stone) => {
        stone.concepts.forEach((concept) => {
          allConcepts.push(concept)
        })
      })

      let index = allConcepts.indexOf(this.clickedConcept)

      let circularIndex = (index - 1 < 0) ? allConcepts.length - 1 : (index - 1)

      let nextConcept = allConcepts[circularIndex]
      this.clickedConcept = nextConcept
    },

    onAboutClose () {
      this.openAbout = false
    },

    switchAR: function () {
      if (this.currentMode === 'ar') {
        if (!this.arIntro) {
          this.arIntro = true
          return
        }
      }

      this.clickedConcept = null
      this.tooltips = []
      this.currentMode = this.currentMode === 'ar' ? 'totem' : 'ar'
    },

    gotoSaveTotemForm: async function () {
      this.clickedConcept = null
      this.currentMode = 'save'

      // retrieve related totems
      let related = await this.backend.getRelatedTotems(this.selectedConcepts)

      // console.log('related:', related)

      let relatedData = []
      // const maxTotems = 5
      const middle = (related.length - 1) / 2
      const spacing = 5
      related.forEach((r, i) => {
        console.log('related:', r, i)
        let totem = this.backend.getTotemData(r)
        totem.id = 'related-totem ' + i + Math.random()
        totem.label = r[0].name
        totem.position = {x: spacing * (i - middle), z: -10}
        relatedData.push(totem)
      })
      this.relatedTotems = relatedData
    }
  },

  watch: {
    currentMode: function (value) {
      if (value === 'wheel') {
        this.introTextVisible = true
        delay(() => {
          this.introTextVisible = false
        }, 2000)
      }
    },
    isReady: function (value) {
      // hide / show preloader
      this.preloader.visible = !value && this.backend.loading
    },
    'backend.loading': function (value) {
      // hide / show preloader
      this.preloader.visible = value || !this.isReady
    }
  },

  metaInfo: function () {
    return {
      // if no subcomponents specify a metaInfo.title, this title will be used
      title: 'Building Hopes',
      // all titles will be injected into this template
      // titleTemplate: '%s | ' + 'Google Hope',
      base: { href: '/' },
      meta: [
        { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
        { name: 'fragment', content: '!' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'mobile-web-app-capable', content: 'yes' },
        { name: 'msapplication-tap-highlight', content: 'no' },
        { name: 'msapplication-TileColor', content: '#100E17' },
        { name: 'msapplication-TileImage', content: '/ms-icon-144x144.png' },
        { name: 'theme-color', content: '#100E17' },
        // twitter
        { name: 'twitter:card', content: 'photo' },
        { name: 'twitter:title', content: 'What are you hopeful for?' },
        { name: 'twitter:image', content: this.targetDomain + '/static/img/share-tw.jpg' },
        { name: 'twitter:description', content: 'Build a sculpture with your hopes for the future and let it live forever in place that is meaningful for you, for others to find, explore, and interact with.' },
        // generic description
        { vmid: 'description', name: 'description', content: 'Build a sculpture with your hopes for the future and let it live forever in place that is meaningful for you, for others to find, explore, and interact with.' },
        // og tags
        { vmid: 'ogtype', property: 'og:type', content: 'website' },
        { vmid: 'ogurl', property: 'og:url', content: this.targetDomain + this.$route.fullPath + '/' },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: 'What are you hopeful for?' },
        { vmid: 'ogdescription', property: 'og:description', content: 'Build a sculpture with your hopes for the future and let it live forever in place that is meaningful for you, for others to find, explore, and interact with.' },
        { vmid: 'ogimage', property: 'og:image', content: this.targetDomain + '/static/img/share-fb.jpg' }
      ],
      htmlAttrs: {
        lang: this.$language.current
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import 'sprite';
@import 'mixins';
@import 'variables';

.selection-wheel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: #{$header-height}px;
  font-weight: 700;
  color: $c-gray;
  font-size: 14px;
  background-color: #f9f8f7;
  border-bottom: 1px solid $c-silver;
  box-sizing: border-box;

  &.in-totem {
    display: none;
  }

  &.page-about {
    z-index: $z-header;
  }

  @include breakpoint(#{$bp-sm}px) {
    // border: 0;
    // box-shadow: 0px 2px 10px color-alpha($c-black, 0.1);
    &.in-totem {
      display: block;
      z-index: 1;
    }
  }

  .logo {
    display: none;
    position: absolute;
    left: 20px;
    top: 0;
    line-height: #{$header-height}px;
    z-index: $z-ui;
    cursor: none;
    pointer-events: none;
    user-select: none;
    letter-spacing: 2px;
    font-weight: 600;

    @include breakpoint(#{$bp-sm}px) {
      display: block;
    }
  }
  .about {
    display: none;
    position: absolute;
    right: 0;
    padding-left: 20px;
    padding-right: 20px;
    top: 0;
    line-height: #{$header-height}px;
    cursor: pointer;
    z-index: $z-ui;
    letter-spacing: 2px;
    font-weight: 600;
    @include breakpoint(#{$bp-sm}px) {
      display: block;
    }
  }
  .share-desktop {
    display: none;
    position: absolute;
    right: 90px;
    top: 0;
    line-height: #{$header-height}px;
    padding-left: 20px;
    padding-right: 20px;
    cursor: pointer;
    z-index: $z-ui;
    letter-spacing: 2px;
    font-weight: 600;
    @include breakpoint(#{$bp-sm}px) {
      display: block;
    }
  }
  .about-mobile {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    font-size: 20px;
    line-height: #{$header-height}px;
    border-left: 1px solid $c-silver;
    width: 50px;
    text-align: center;
    z-index: $z-header;
    letter-spacing: 2px;
    font-weight: 600;

    @include breakpoint(#{$bp-sm}px) {
      display: none;
    }
  }
}

.totem-canvas {
  position: absolute;
  z-index: 1;

  &.totem-visualization {
    z-index: 0;
  }
}


.wheel-done-bg {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 40px;
  margin: auto;
  z-index: $z-ui;
  background-color: #ddd;
  width: 100px;
  height: 38px;
  border-radius: 4px;

  animation: pulse 2.0s ease;
  animation-iteration-count: 3;
}

.wheel-done-btn {
  position: absolute;
  left: 50%;
  bottom: 40px;
  transform: translateX(-50%);
  z-index: $z-ui;

  overflow: visible;

  @include breakpoint(#{$bp-sm}px) {
    position: absolute;
    left: 0;
    right: 0;
    transform: none;
    margin: 0 auto;
    display: table;
    width: 100px;
    bottom: 40px;
  }

  // &::after {
  //   position: absolute;
  //   content: ' ';
  //   display: block;

  //   width: 100%;
  //   height: 100%;

  //   top: 0;
  //   left: 0;

  //   background-color: #808184;
  //   transform: rotate(-45deg) scaleY(1);

  //   animation: wheelDoneBtn 0.7s linear 0.2s forwards;
  // }
}


.stones-container {
  position: absolute;
  top: 0;
  left: #{$concept-container-mobile-left}px;
  right: #{$concept-container-mobile-right}px;
  height: 50px;
  // width: 90%;
  height: #{$header-height}px;

  @include breakpoint(#{$bp-sm}px) {
    width: #{$concept-container-desktop-width}px;
    left: 50%;
    right: inherit;
    transform: translate(-50%, 0);
  }
}

.ar-view-btn {
  position: absolute;
  z-index: $z-ui;
  top: 10px;
  right: 65px;
  @include breakpoint(#{$bp-sm}px) {
    top: #{$header-height + 10}px;
  }
}

.info-btn {
  position: absolute;
  z-index: 2;
  top: 10px;
  right: 10px;
  border-radius: 50%;
  padding: 0;
  width: 38px;
  height: 38px;

  @include breakpoint(#{$bp-sm}px) {
    top: #{$header-height + 10}px;
  }  
}

.back-from-related-totems-btn {
  position: absolute;
  z-index: $z-ui;
  top: 20px;
  left: 20px;
}

.about-label {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  margin: 0 auto;
  width: 80px;
  text-align: center;
  letter-spacing: 1px;
  font-size: 16px;
  display: block;

  @include breakpoint(#{$bp-sm}px) {
    display: none;
  }
}

.intro-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  // temp
  @include breakpoint(#{$bp-sm}px) {
    font-size: 18px;
  }
  letter-spacing: 1px;
  color: $c-gray;
  pointer-events: none;
  width: 100%;
  box-sizing: border-box;
  padding: 0 10px;
  text-align: center;
}


// .intro-enter-active,
.intro-leave-active {
  transition: opacity 0.5s;
}

// .intro-enter,
.intro-leave-to {
  opacity: 0;
}

.intro-text-enter-active {
  transition: opacity 0.5s;
}
.intro-text-leave-active {
  transition: opacity 3s;
}

.intro-text-enter,
.intro-text-leave-to {
  opacity: 0;
}

.wheel-enter-active {
  transition: opacity 3s $ease-in-out-quad 0.5s;
}
.wheel-leave-active {
  transition: opacity 0.5s $ease-in-out-quad;
}

.wheel-enter,
.wheel-leave-to {
  opacity: 0;
}

.concept-complete-enter-active {
  transition: opacity 3s $ease-in-out-quad 0.5s;
}
.concept-complete-leave-active {
  transition: opacity 0.2s $ease-in-out-quad;
}

.concept-complete-enter,
.concept-complete-leave-to {
  opacity: 0;
}

.concept-details {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: $z-ui;
  // transform: translate(0, 0);

  @include breakpoint(#{$bp-sm}px) {
    width: 50%;
    left: 50%;
    box-shadow: 0px 0px 50px color-alpha($c-black, 0.3);
  }
}

.ar-close-btn {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  top: 10px;
  left: 10px;
  background: color-alpha($c-black, 0.25);
  color: $c-white;
  font-size: 30px;
  line-height: 40px;
  text-align: center;
  z-index: 2;
}
.ar-download-marker-btn {
  position: absolute;
  height: 40px;
  border-radius: 10px;
  top: 10px;
  right: 10px;
  background: color-alpha($c-black, 0.25);
  color: $c-white;
  line-height: 40px;
  text-align: center;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 16px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
}

.totem-dots {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translate(0, -50%);
  height: 50px;
  @include breakpoint(#{$bp-sm}px) {
    left: 40px;
  }
}

.tooltip {
  position: absolute;
  width: 0;
  height: 0;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
}

.tooltip-internal {
  position: absolute;
  transform: translate(5px, -50%);
  padding: 10px;
  border-radius: 4px;
  background-color: $c-white;
  // text-transform: uppercase;
  letter-spacing: 1px;
  // font-size: 10px;
  font-size: 12px;
  // min-width: 200px;
  text-align: center;
  // padding: 5px;
  padding: 6px;

  @include breakpoint(#{$bp-sm}px) {
    font-size: 14px;
    padding: 10px;
  }
}

.details-panel-enter-active,
.details-panel-leave-active {
  transition: opacity 0.5s $ease-in-out-quad, transform 0.4s $ease-in-out-quad;
}

.details-panel-enter,
.details-panel-leave-to {
  opacity: 0;

  @include breakpoint(#{$bp-sm}px) {
    opacity: 1;
    transform: translate(100%, 0);
  }
}

.wheel-done-enter-active {
  transition: opacity 0.5s $ease-in-out-quad 0.1s;
}

.wheel-done-leave-active {
  transition: opacity 0.1s $ease-in-out-quad;
}

.wheel-done-enter,
.wheel-done-leave-to {
  opacity: 0;
}

@keyframes wheelDoneBtn {
  0% {
    transform: rotate(-45deg) scaleY(1);
    opacity: 0;
  }

  25% {
    transform: rotate(-45deg) scaleY(1);
    opacity: 1;
  }

  55% {
    transform: rotate(-45deg) scaleY(4);
    opacity: 1;
  }

  100% {
    transform: rotate(-45deg) scaleY(4);
    opacity: 0;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1, 1);
    opacity: 0.6;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    transform: scale(1.1, 1.3);
    opacity: 0;
  }
}

</style>

<style lang="scss">
// either use this (to normalize css)
// @import '~normalize-scss';
// or this (to reset css)
@import '~node-reset-scss/scss/reset';
// IMPORTANT: don't import both
@import '~element-ui/lib/theme-chalk/index.css';


@import 'variables';


@import 'fonts';
@import 'mixins';
@import 'sprite';


//
//
// Default styles
// NOTE: delete these and re-write them in your app
//
//

html,
body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  overflow: hidden;
  position: fixed;
  margin: 0;
}

p {
  margin: 0.5rem;
}

a {
  color: $c-black;
  text-decoration: none;

  &:hover {
    color: $c-black;
  }
}

strong,
em {
  font-weight: 600;
}

.hover-link {
  transition: opacity 0.3s $ease-out-quad;

  &:hover {
    opacity: 0.6;
  }
}

.el-button {
  border-radius: 4px;
  border: 0;
  letter-spacing: 1px;
  background: $c-white;
  color: $c-gray;
  font-weight: 600;
  // font-weight: 500;
  padding: 12px 30px;
  transition: color 0.4s $ease-in-out-quad, background 0.4s $ease-in-out-quad, opacity 0.3s $ease-out-quad;
  box-shadow: 0px 3px 3px color-alpha($c-black, 0.1);

  &:hover,
  &:focus {
    background: $c-silver;
    color: $c-gray;
  }

  &.secondary {
    box-shadow: initial;
    background: transparent;
    &:hover,
    &:focus {
      background: $c-silver;
      color: $c-gray;
    }
  }
}

.el-input__inner {
  text-align: center;
  // text-transform: uppercase;
  border-radius: 0;
  border: 0;
  border-bottom: 1px solid #8a8a89;
  background-color: transparent;
  font-family: 'Muli', sans-serif;
  font-size: 19px;
  letter-spacing: 1px;

  transition: border-color 0.3s $ease-out-quad;

  &:focus {
    border-color: #b1b1b1;
  }

  &:hover {
    border-color: #b1b1b1;
  }
}

//
//
// this is the default transition
// when a component is enclosed in a <transition> tab with no "name" specified
// this is the transition which is triggered by default
//
//
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s;
}

.v-enter,
.v-leave-to {
  opacity: 0;
}

</style>



// WEBPACK FOOTER //
// src/App.vue